import React, { useMemo, useState } from "react";
import { MOCK_DATASET } from "../../imports/mock-benchmark";
import type { RunStatus } from "../../imports/mock-benchmark";
import { buildMatrixDatasetView } from "../../imports/mock-benchmark/chartData";
import type { ModelStat } from "../../imports/mock-benchmark/chartData";

/* ─── layout constants ─────────────────────────── */
const LABEL_W   = 180;   // px – fixed left column
const CELL_W    = 13;    // px – cell width
const STRIP_H   = 4;     // px – height of one run-strip
const STRIP_GAP = 1;     // px – gap between strips
const CELL_H    = STRIP_H * 3 + STRIP_GAP * 2;  // 14px total cell height
const CELL_SIZE = CELL_W; // alias kept for header width calc
const CELL_GAP  = 2;     // px – gap between cells
const CELL_STEP = CELL_W + CELL_GAP;
const ROW_H     = CELL_H;
const HDR_H     = 52;    // column-label header height (rotated text)

const BORDER = "1px solid rgba(59,14,39,0.1)";  // brand border at 10% opacity

type MatrixMode = "correct" | "output";

/* ─── colour helpers ────────────────────────────── */
const GREEN_FULL  = "rgba(60,160,80,1)";
const GREEN_DIM   = "rgba(60,160,80,0.35)";
const RED_FULL    = "rgba(200,65,65,1)";
const RED_DIM     = "rgba(200,65,65,0.35)";
const BLUE_FULL   = "rgba(60,110,210,1)";
const BLUE_DIM    = "rgba(60,110,210,0.35)";
const AMBER_FULL  = "rgba(200,130,50,1)";
const AMBER_DIM   = "rgba(200,130,50,0.35)";
const GRAY_FULL   = "rgba(140,140,140,0.75)";
const GRAY_DIM    = "rgba(140,140,140,0.28)";

/** Per-run strip colour */
function runStripColor(
  mode: MatrixMode,
  status: RunStatus,
  output: boolean | null,
  rowActive: boolean,
  colActive: boolean,
): string {
  const full = rowActive || colActive;
  if (status === "crashed") return full ? GRAY_FULL : GRAY_DIM;
  if (mode === "correct") {
    return status === "correct"
      ? (full ? GREEN_FULL : GREEN_DIM)
      : (full ? RED_FULL   : RED_DIM);
  }
  // output mode
  return output
    ? (full ? BLUE_FULL  : BLUE_DIM)
    : (full ? AMBER_FULL : AMBER_DIM);
}

function accuracyColor(pct: number) {
  return pct >= 85 ? GREEN_FULL : pct >= 70 ? AMBER_FULL : RED_FULL;
}

/* ─── small atoms ───────────────────────────────── */
function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="flex flex-col gap-[4px] px-[20px] py-[16px]"
      style={{ border: BORDER }}>
      <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px] tracking-[0.4px]"
        style={{ color: "var(--brand-text)", opacity: 0.38 }}>{label}</span>
      <span className="font-['Helvetica_Neue:Condensed_Bold',sans-serif] text-[28px] tracking-[-1px] leading-none"
        style={{ color: "var(--brand-text)" }}>{value}</span>
      {sub && <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px]"
        style={{ color: "var(--brand-text)", opacity: 0.32 }}>{sub}</span>}
    </div>
  );
}

function AccBar({ value, width = 80 }: { value: number; width?: number }) {
  const pct = Math.round(value * 100);
  return (
    <div className="flex items-center gap-[6px]">
      <div className="h-[3px] relative flex-1" style={{ backgroundColor: "var(--brand-border-faint)" }}>
        <div className="absolute inset-y-0 left-0" style={{ width: `${pct}%`, backgroundColor: accuracyColor(pct) }} />
      </div>
      <span className="font-['Helvetica_Neue:Medium',sans-serif] text-[11px] tabular-nums"
        style={{ color: accuracyColor(pct), width: 30, textAlign: "right", flexShrink: 0 }}>
        {pct}%
      </span>
    </div>
  );
}

/* ─── Rankings ──────────────────────────────────── */
function Rankings({ stats, activeId, onSelect }: {
  stats: ModelStat[];
  activeId: string;
  onSelect: (id: string) => void;
}) {
  const sorted = [...stats].sort((a, b) => b.accuracy - a.accuracy);
  return (
    <div>
      {/* header */}
      <div className="grid py-[6px]"
        style={{
          gridTemplateColumns: "24px 1fr 90px 60px 110px",
          columnGap: 16,
          borderBottom: BORDER,
        }}>
        {["#", "Model", "Provider", "Correct", "Accuracy"].map((h, i) => (
          <span key={h}
            className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px] tracking-[0.3px]"
            style={{ color: "var(--brand-text)", opacity: 0.3, textAlign: i >= 3 ? "right" : "left" }}>
            {h}
          </span>
        ))}
      </div>
      {sorted.map((s, idx) => {
        const active = s.id === activeId;
        return (
          <button key={s.id} onClick={() => onSelect(s.id)}
            className="w-full grid items-center py-[10px] bg-transparent border-none cursor-pointer text-left"
            style={{
              gridTemplateColumns: "24px 1fr 90px 60px 110px",
              columnGap: 16,
              borderBottom: BORDER,
              backgroundColor: active ? "rgba(59,14,39,0.03)" : "transparent",
            }}>
            <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] tabular-nums"
              style={{ color: "var(--brand-text)", opacity: 0.35 }}>{idx + 1}</span>
            <span className={`font-['Helvetica_Neue:${active ? "Medium" : "Regular"}',sans-serif] text-[13px]`}
              style={{ color: "var(--brand-text)" }}>{s.name}</span>
            <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px]"
              style={{ color: "var(--brand-text)", opacity: 0.4 }}>{s.vendor}</span>
            <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] tabular-nums text-right"
              style={{ color: "var(--brand-text)", opacity: 0.6 }}>{s.correct}/{s.total}</span>
            <AccBar value={s.accuracy} />
          </button>
        );
      })}
    </div>
  );
}

/* ─── Detail tooltip panel ──────────────────────── */
function DetailPanel({
  modelId, problemId, mode,
}: {
  modelId: string;
  problemId: number | null;
  mode: MatrixMode;
}) {
  const model   = MOCK_DATASET.models.find(m => m.id === modelId);
  const problem = problemId != null ? MOCK_DATASET.problems.find(p => p.id === problemId) : null;
  const cell    = model && problem ? model.results[problem.id] : null;

  if (!model) return null;

  const totalCorrect = Object.values(model.results).filter(r => r.correct).length;
  const total        = MOCK_DATASET.problems.length;

  const RUN_LABELS = ["Run 1", "Run 2", "Run 3"];

  return (
    <div className="flex flex-wrap gap-[1px]" style={{ backgroundColor: "var(--brand-border-faint)" }}>
      {/* model summary */}
      <div className="flex-1 min-w-[160px] p-[16px]" style={{ backgroundColor: "var(--brand-bg)" }}>
        <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[10px] tracking-[0.5px] m-0 mb-[8px]"
          style={{ color: "var(--brand-text)", opacity: 0.3 }}>SELECTED MODEL</p>
        <p className="font-['Helvetica_Neue:Medium',sans-serif] text-[15px] m-0"
          style={{ color: "var(--brand-text)" }}>{model.name}</p>
        <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0 mt-[2px] mb-[10px]"
          style={{ color: "var(--brand-text)", opacity: 0.4 }}>{model.vendor}</p>
        <AccBar value={totalCorrect / total} />
        <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px] m-0 mt-[6px]"
          style={{ color: "var(--brand-text)", opacity: 0.4 }}>
          {totalCorrect} / {total} correct
        </p>
      </div>

      {/* cell result */}
      {cell && problem && (
        <div className="flex-1 min-w-[200px] p-[16px]" style={{ backgroundColor: "var(--brand-bg)" }}>
          <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[10px] tracking-[0.5px] m-0 mb-[10px]"
            style={{ color: "var(--brand-text)", opacity: 0.3 }}>PROBLEM P{problem.id} — 3 RUNS</p>
          <div className="flex flex-col gap-[7px]">
            {cell.runs.map((status, ri) => {
              const out = cell.outputs[ri];
              const statusColor =
                status === "crashed" ? GRAY_FULL :
                status === "correct" ? GREEN_FULL : RED_FULL;
              const label =
                status === "crashed"   ? "Crashed" :
                status === "correct"   ? "✓ Correct" : "✗ Wrong";
              return (
                <div key={ri} className="flex items-center gap-[8px]">
                  <div style={{ width: 8, height: 8, backgroundColor: statusColor, flexShrink: 0 }} />
                  <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px]"
                    style={{ color: "var(--brand-text)", opacity: 0.38, width: 36, flexShrink: 0 }}>
                    {RUN_LABELS[ri]}
                  </span>
                  <span className="font-['Helvetica_Neue:Medium',sans-serif] text-[11px]"
                    style={{ color: statusColor }}>
                    {label}
                  </span>
                  {out !== null && (
                    <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[10px] ml-auto"
                      style={{ color: "var(--brand-text)", opacity: 0.3 }}>
                      → {out ? "TRUE" : "FALSE"}
                    </span>
                  )}
                </div>
              );
            })}
            <div className="flex justify-between items-baseline pt-[6px]"
              style={{ borderTop: BORDER }}>
              <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px]"
                style={{ color: "var(--brand-text)", opacity: 0.38 }}>Expected</span>
              <span className="font-['Helvetica_Neue:Medium',sans-serif] text-[12px]"
                style={{ color: "var(--brand-text)" }}>
                {problem.expected ? "TRUE" : "FALSE"}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Main page ─────────────────────────────────── */
export function MockBenchmarkPage() {
  const view = useMemo(() => buildMatrixDatasetView(MOCK_DATASET), []);

  const [mode,            setMode]            = useState<MatrixMode>("correct");
  const [activeModelId,   setActiveModelId]   = useState<string>(MOCK_DATASET.models[0]?.id ?? "");
  const [activeProblemId, setActiveProblemId] = useState<number | null>(null);

  const problems = MOCK_DATASET.problems;
  const models   = useMemo(() => {
    return [...MOCK_DATASET.models].sort((a, b) => {
      const statA = view.modelStats.find(s => s.id === a.id)?.accuracy ?? 0;
      const statB = view.modelStats.find(s => s.id === b.id)?.accuracy ?? 0;
      return statB - statA;
    });
  }, [view.modelStats]);
  const overallPct = Math.round(view.overallAccuracy * 100);
  const best = [...view.modelStats].sort((a, b) => b.accuracy - a.accuracy)[0];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--brand-bg)" }}>
      <div className="px-[24px] pt-[40px] pb-[80px] max-w-[1440px] mx-auto">

        {/* ── header ──────────────────────────────── */}
        <div className="mb-[32px]">
          <div className="flex items-start justify-between gap-[16px] flex-wrap">
            <div>
              <h1 className="font-['Helvetica_Neue:Condensed_Bold',sans-serif] text-[40px] md:text-[52px] tracking-[-2px] leading-none m-0 mb-[8px]"
                style={{ color: "var(--brand-text)" }}>
                Mock Benchmark
              </h1>
              <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] leading-[1.6] m-0 max-w-[520px]"
                style={{ color: "var(--brand-text)", opacity: 0.5 }}>
                Equational theories evaluation — does Eq. 1 imply Eq. 2 over all magmas? Models answer TRUE or FALSE per problem.
              </p>
            </div>
            <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] px-[10px] py-[3px] self-start mt-[6px]"
              style={{ color: "var(--brand-text)", opacity: 0.45 }}>
              March 2026
            </span>
          </div>
        </div>

        {/* ── stat cards ──────────────────────────── */}
        <div className="grid grid-cols-4 mb-[36px]">
          <StatCard label="Models"           value={String(models.length)}   sub="evaluated" />
          <StatCard label="Problems"         value={String(problems.length)} sub="Stage 1" />
          <StatCard label="Avg. Accuracy"    value={`${overallPct}%`}        sub={`${models.length} models`} />
          <StatCard label="Best Model"       value={`${Math.round((best?.accuracy ?? 0) * 100)}%`} sub={best?.name} />
        </div>

        {/* ── matrix ──────────────────────────────── */}
        <div className="mb-[40px]">
          {/* toolbar */}
          <div className="flex items-center justify-between mb-[12px] gap-[12px] flex-wrap">
            <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px] tracking-[0.7px]"
              style={{ color: "var(--brand-text)", opacity: 0.3 }}>
              {mode === "correct" ? "CORRECTNESS MATRIX" : "OUTPUT MATRIX"} — {models.length} models × {problems.length} problems
            </span>
            <div className="flex" style={{ border: BORDER }}>
              {(["correct", "output"] as MatrixMode[]).map((m) => (
                <button key={m} onClick={() => setMode(m)}
                  className="px-[14px] h-[28px] cursor-pointer border-none"
                  style={{ backgroundColor: mode === m ? "var(--brand-primary-btn-bg)" : "transparent" }}>
                  <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px]"
                    style={{
                      color: mode === m ? "var(--brand-primary-btn-text)" : "var(--brand-text)",
                      opacity: mode === m ? 1 : 0.5,
                    }}>
                    {m === "correct" ? "Correctness" : "Output"}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div style={{ border: BORDER, position: "relative" }}>
            <div style={{ overflowX: "auto", overflowY: "visible" }}>
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <div style={{
                  position: "sticky", left: 0, zIndex: 30, flexShrink: 0,
                  width: LABEL_W, height: HDR_H,
                  backgroundColor: "var(--brand-bg)",
                  borderRight: BORDER,
                  display: "flex", alignItems: "flex-end", paddingBottom: 4, paddingLeft: 12,
                }}>
                  <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[10px] tracking-[0.3px]"
                    style={{ color: "var(--brand-text)", opacity: 0.28 }}>MODEL</span>
                </div>
                <div style={{ display: "flex", paddingLeft: CELL_GAP, paddingTop: 4, paddingBottom: 0, flexShrink: 0 }}>
                  {problems.map((p) => {
                    const isActive = activeProblemId === p.id;
                    return (
                      <button key={p.id} onClick={() => setActiveProblemId(isActive ? null : p.id)}
                        style={{
                          width: CELL_STEP, height: HDR_H, background: "transparent", border: "none", cursor: "pointer",
                          padding: 0, display: "flex", alignItems: "flex-end", justifyContent: "center", paddingBottom: 5, flexShrink: 0,
                        }}>
                        <span style={{
                          writingMode: "vertical-rl", transform: "rotate(180deg)",
                          fontFamily: "'Helvetica Neue', sans-serif", fontSize: 10,
                          color: "var(--brand-text)", opacity: isActive ? 1 : 0.32, fontWeight: isActive ? 600 : 400, lineHeight: 1,
                        }}>P{p.id}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              {models.map((model) => {
                const isActiveRow = model.id === activeModelId;
                const stat = view.modelStats.find(s => s.id === model.id);
                const rowPct = stat ? Math.round(stat.accuracy * 100) : 0;
                return (
                  <div key={model.id} style={{
                    display: "flex", alignItems: "center", borderTop: BORDER,
                    backgroundColor: isActiveRow ? "rgba(59,14,39,0.025)" : "var(--brand-bg)", minHeight: ROW_H + 12,
                  }}>
                    <div style={{
                      position: "sticky", left: 0, zIndex: 20, flexShrink: 0, width: LABEL_W,
                      backgroundColor: isActiveRow ? "rgba(59,14,39,0.025)" : "var(--brand-bg)",
                      borderRight: BORDER, height: "100%", display: "flex", alignItems: "center",
                    }}>
                      <button onClick={() => setActiveModelId(model.id)} style={{
                        background: "transparent", border: "none", cursor: "pointer", width: "100%", height: "100%",
                        padding: "8px 12px", display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", gap: 3,
                      }}>
                        <span style={{
                          fontFamily: "'Helvetica Neue', sans-serif", fontSize: 13,
                          fontWeight: isActiveRow ? 600 : 400, color: "var(--brand-text)", opacity: isActiveRow ? 1 : 0.65,
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: LABEL_W - 24, display: "block",
                        }}>{model.name}</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 5, width: "100%" }}>
                          <div style={{ flex: 1, height: 2, backgroundColor: "var(--brand-border-faint)", position: "relative" }}>
                            <div style={{ position: "absolute", inset: 0, width: `${rowPct}%`, backgroundColor: accuracyColor(rowPct) }} />
                          </div>
                          <span style={{ fontFamily: "'Helvetica Neue', sans-serif", fontSize: 10, color: accuracyColor(rowPct), flexShrink: 0 }}>{rowPct}%</span>
                        </div>
                      </button>
                    </div>
                    <div style={{
                      display: "flex", alignItems: "center", paddingLeft: CELL_GAP, paddingTop: 6, paddingBottom: 6, paddingRight: CELL_GAP,
                      flexShrink: 0, gap: CELL_GAP, position: "relative", zIndex: 1,
                    }}>
                      {problems.map((problem) => {
                        const r = model.results[problem.id];
                        if (!r) return null;
                        const isActiveCol = activeProblemId === problem.id;
                        const isSelected = isActiveRow && isActiveCol;
                        return (
                          <button key={problem.id}
                            onClick={() => {
                              setActiveModelId(model.id);
                              setActiveProblemId(activeModelId === model.id && activeProblemId === problem.id ? null : problem.id);
                            }}
                            title={`${model.name} / P${problem.id}`}
                            style={{
                              width: CELL_W, height: CELL_H, border: "none", cursor: "pointer", padding: 0, flexShrink: 0,
                              display: "flex", flexDirection: "column", gap: STRIP_GAP,
                              outline: isSelected ? "2px solid var(--brand-text)" : "none", outlineOffset: 1, background: "transparent",
                            }}>
                            {(() => {
                              const hasCrash = r.runs.some(s => s === "crashed");
                              const allSame = r.runs.every(s => s === r.runs[0]);
                              if (hasCrash) {
                                const full = isActiveRow || isActiveCol;
                                return <div style={{ width: CELL_W, height: CELL_H, flexShrink: 0, backgroundColor: full ? GRAY_FULL : GRAY_DIM }} />;
                              }
                              if (allSame) {
                                const solidColor = runStripColor(mode, r.runs[0], r.outputs[0], isActiveRow, isActiveCol);
                                return <div style={{ width: CELL_W, height: CELL_H, flexShrink: 0, backgroundColor: solidColor }} />;
                              }
                              return r.runs.map((status, ri) => (
                                <div key={ri} style={{ width: CELL_W, height: STRIP_H, flexShrink: 0, backgroundColor: runStripColor(mode, status, r.outputs[ri], isActiveRow, isActiveCol) }} />
                              ));
                            })()}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-[20px] mt-[10px] flex-wrap">
            {mode === "correct" ? (
              <>
                <LegendDot color={GREEN_FULL} label="Correct" />
                <LegendDot color={RED_FULL}   label="Incorrect" />
                <LegendDot color={GRAY_FULL}  label="Crashed" />
              </>
            ) : (
              <>
                <LegendDot color={BLUE_FULL}  label="Output TRUE" />
                <LegendDot color={AMBER_FULL} label="Output FALSE" />
                <LegendDot color={GRAY_FULL}  label="Crashed" />
              </>
            )}
            <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px] ml-auto"
              style={{ color: "var(--brand-text)", opacity: 0.3 }}>
              Click model name to select row · click cell or column label to inspect
            </span>
          </div>
        </div>

        {/* ── detail panel ────────────────────────── */}
        {activeModelId && (
          <div className="mb-[40px]">
            <DetailPanel modelId={activeModelId} problemId={activeProblemId} mode={mode} />
          </div>
        )}

        {/* ── rankings ────────────────────────────── */}
        <div>
          <h2 className="font-['Helvetica_Neue:Condensed_Bold',sans-serif] text-[22px] tracking-[-0.5px] m-0 mb-[14px]"
            style={{ color: "var(--brand-text)" }}>
            Model Rankings
          </h2>
          <div style={{ borderTop: BORDER }}>
            <Rankings
              stats={view.modelStats}
              activeId={activeModelId}
              onSelect={(id) => setActiveModelId(id)}
            />
          </div>
        </div>

      </div>
    </div>
  );
}

/* ─── Embeddable section (no page wrapper, no rankings) ── */
export function MockBenchmarkSection() {
  const view = useMemo(() => buildMatrixDatasetView(MOCK_DATASET), []);

  const [mode,            setMode]            = useState<MatrixMode>("correct");
  const [activeModelId,   setActiveModelId]   = useState<string>(MOCK_DATASET.models[0]?.id ?? "");
  const [activeProblemId, setActiveProblemId] = useState<number | null>(null);

  const problems = MOCK_DATASET.problems;
  const models   = useMemo(() => {
    return [...MOCK_DATASET.models].sort((a, b) => {
      const statA = view.modelStats.find(s => s.id === a.id)?.accuracy ?? 0;
      const statB = view.modelStats.find(s => s.id === b.id)?.accuracy ?? 0;
      return statB - statA;
    });
  }, [view.modelStats]);
  const overallPct = Math.round(view.overallAccuracy * 100);
  const best = [...view.modelStats].sort((a, b) => b.accuracy - a.accuracy)[0];

  return (
    <>
      {/* ── header ──────────────────────────────── */}
      <div className="mb-[32px]">
        <div className="flex items-start justify-between gap-[16px] flex-wrap">
          <div>
            <h1 className="font-['Helvetica_Neue:Condensed_Bold',sans-serif] tracking-[-2px] leading-none m-0 mb-[8px] text-[48px]"
              style={{ color: "var(--brand-text)" }}>
              Mock Benchmark
            </h1>
            <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] leading-[1.6] m-0 max-w-[520px]"
              style={{ color: "var(--brand-text)", opacity: 0.5 }}>
              Equational theories evaluation — does Eq. 1 imply Eq. 2 over all magmas? Models answer TRUE or FALSE per problem.
            </p>
          </div>
          
        </div>
      </div>

      {/* ── stat cards ──────────────────────────── */}
      <div className="grid grid-cols-4 mb-[36px]">
        <StatCard label="Models"           value={String(models.length)}   sub="evaluated" />
        <StatCard label="Problems"         value={String(problems.length)} sub="Stage 1" />
        <StatCard label="Avg. Accuracy"    value={`${overallPct}%`}        sub={`${models.length} models`} />
        <StatCard label="Best Model"       value={`${Math.round((best?.accuracy ?? 0) * 100)}%`} sub={best?.name} />
      </div>

      {/* ── matrix ──────────────────────────────── */}
      <div className="mb-[40px]">
        {/* toolbar */}
        <div className="flex items-center justify-between mb-[12px] gap-[12px] flex-wrap">
          <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px] tracking-[0.7px]"
            style={{ color: "var(--brand-text)", opacity: 0.3 }}>
            {mode === "correct" ? "CORRECTNESS MATRIX" : "OUTPUT MATRIX"} — {models.length} models × {problems.length} problems
          </span>
          {/* mode toggle */}
          <div className="flex" style={{ border: BORDER }}>
            {(["correct", "output"] as MatrixMode[]).map((m) => (
              <button key={m} onClick={() => setMode(m)}
                className="px-[14px] h-[28px] cursor-pointer border-none"
                style={{ backgroundColor: mode === m ? "var(--brand-primary-btn-bg)" : "transparent" }}>
                <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px]"
                  style={{
                    color: mode === m ? "var(--brand-primary-btn-text)" : "var(--brand-text)",
                    opacity: mode === m ? 1 : 0.5,
                  }}>
                  {m === "correct" ? "Correctness" : "Output"}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* matrix container */}
        <div style={{ border: BORDER, position: "relative" }}>
          <div style={{ overflowX: "auto", overflowY: "visible" }}>
            {/* ── column-header row ── */}
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              {/* sticky corner */}
              <div style={{
                position: "sticky", left: 0, zIndex: 30, flexShrink: 0,
                width: LABEL_W, height: HDR_H,
                backgroundColor: "var(--brand-bg)",
                borderRight: BORDER,
                display: "flex", alignItems: "flex-end", paddingBottom: 4, paddingLeft: 12,
              }}>
                <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[10px] tracking-[0.3px]"
                  style={{ color: "var(--brand-text)", opacity: 0.28 }}>MODEL</span>
              </div>
              {/* problem labels */}
              <div style={{ display: "flex", paddingLeft: CELL_GAP, paddingTop: 4, paddingBottom: 0, flexShrink: 0 }}>
                {problems.map((p) => {
                  const isActive = activeProblemId === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setActiveProblemId(isActive ? null : p.id)}
                      style={{
                        width: CELL_STEP, height: HDR_H,
                        background: "transparent", border: "none", cursor: "pointer",
                        padding: 0, display: "flex", alignItems: "flex-end", justifyContent: "center",
                        paddingBottom: 5,
                        flexShrink: 0,
                      }}
                    >
                      <span style={{
                        writingMode: "vertical-rl",
                        transform: "rotate(180deg)",
                        fontFamily: "'Helvetica Neue', sans-serif",
                        fontSize: 10,
                        color: "var(--brand-text)",
                        opacity: isActive ? 1 : 0.32,
                        fontWeight: isActive ? 600 : 400,
                        lineHeight: 1,
                      }}>
                        P{p.id}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── body rows ── */}
            {models.map((model) => {
              const isActiveRow = model.id === activeModelId;
              const stat = view.modelStats.find(s => s.id === model.id);
              const rowPct = stat ? Math.round(stat.accuracy * 100) : 0;

              return (
                <div key={model.id} style={{
                  display: "flex", alignItems: "center",
                  borderTop: BORDER,
                  backgroundColor: isActiveRow ? "rgba(59,14,39,0.025)" : "var(--brand-bg)",
                  minHeight: ROW_H + 12,
                }}>
                  {/* sticky model label */}
                  <div style={{
                    position: "sticky", left: 0, zIndex: 20, flexShrink: 0,
                    width: LABEL_W,
                    backgroundColor: isActiveRow ? "rgba(59,14,39,0.025)" : "var(--brand-bg)",
                    borderRight: BORDER,
                    height: "100%",
                    display: "flex", alignItems: "center",
                  }}>
                    <button
                      onClick={() => setActiveModelId(model.id)}
                      style={{
                        background: "transparent", border: "none", cursor: "pointer",
                        width: "100%", height: "100%",
                        padding: "8px 12px 8px 12px",
                        display: "flex", flexDirection: "column",
                        alignItems: "flex-start", justifyContent: "center", gap: 3,
                      }}
                    >
                      <span style={{
                        fontFamily: "'Helvetica Neue', sans-serif",
                        fontSize: 13,
                        fontWeight: isActiveRow ? 600 : 400,
                        color: "var(--brand-text)",
                        opacity: isActiveRow ? 1 : 0.65,
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        maxWidth: LABEL_W - 24,
                        display: "block",
                      }}>
                        {model.name}
                      </span>
                      {/* mini accuracy bar */}
                      <div style={{ display: "flex", alignItems: "center", gap: 5, width: "100%" }}>
                        <div style={{
                          flex: 1, height: 2,
                          backgroundColor: "var(--brand-border-faint)", position: "relative",
                        }}>
                          <div style={{
                            position: "absolute", inset: 0,
                            width: `${rowPct}%`,
                            backgroundColor: accuracyColor(rowPct),
                          }} />
                        </div>
                        <span style={{
                          fontFamily: "'Helvetica Neue', sans-serif",
                          fontSize: 10, color: accuracyColor(rowPct),
                          flexShrink: 0,
                        }}>{rowPct}%</span>
                      </div>
                    </button>
                  </div>

                  {/* cells */}
                  <div style={{
                    display: "flex", alignItems: "center",
                    paddingLeft: CELL_GAP, paddingTop: 6, paddingBottom: 6, paddingRight: CELL_GAP,
                    flexShrink: 0, gap: CELL_GAP,
                    position: "relative", zIndex: 1,
                  }}>
                    {problems.map((problem) => {
                      const r = model.results[problem.id];
                      if (!r) return null;
                      const isActiveCol = activeProblemId === problem.id;
                      const isSelected  = isActiveRow && isActiveCol;

                      return (
                        <button
                          key={problem.id}
                          onClick={() => {
                            setActiveModelId(model.id);
                            setActiveProblemId(activeModelId === model.id && activeProblemId === problem.id ? null : problem.id);
                          }}
                          title={`${model.name} / P${problem.id} — ${r.runs.map((s, i) => `Run${i+1}:${s}`).join(" ")}`}
                          style={{
                            width: CELL_W, height: CELL_H,
                            border: "none", cursor: "pointer", padding: 0, flexShrink: 0,
                            display: "flex", flexDirection: "column", gap: STRIP_GAP,
                            outline: isSelected ? "2px solid var(--brand-text)" : "none",
                            outlineOffset: 1,
                            background: "transparent",
                          }}
                        >
                          {(() => {
                            const hasCrash = r.runs.some(s => s === "crashed");
                            const allSame  = r.runs.every(s => s === r.runs[0]);

                            if (hasCrash) {
                              const full = isActiveRow || isActiveCol;
                              return (
                                <div style={{ width: CELL_W, height: CELL_H, flexShrink: 0, backgroundColor: full ? GRAY_FULL : GRAY_DIM }} />
                              );
                            }

                            if (allSame) {
                              const solidColor = runStripColor(mode, r.runs[0], r.outputs[0], isActiveRow, isActiveCol);
                              return (
                                <div style={{ width: CELL_W, height: CELL_H, flexShrink: 0, backgroundColor: solidColor }} />
                              );
                            }

                            return r.runs.map((status, ri) => (
                              <div
                                key={ri}
                                style={{
                                  width: CELL_W,
                                  height: STRIP_H,
                                  flexShrink: 0,
                                  backgroundColor: runStripColor(mode, status, r.outputs[ri], isActiveRow, isActiveCol),
                                }}
                              />
                            ));
                          })()}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* legend + hint */}
        <div className="flex items-center gap-[20px] mt-[10px] flex-wrap">
          {mode === "correct" ? (
            <>
              <LegendDot color={GREEN_FULL} label="Correct" />
              <LegendDot color={RED_FULL}   label="Incorrect" />
              <LegendDot color={GRAY_FULL}  label="Crashed" />
            </>
          ) : (
            <>
              <LegendDot color={BLUE_FULL}  label="Output TRUE" />
              <LegendDot color={AMBER_FULL} label="Output FALSE" />
              <LegendDot color={GRAY_FULL}  label="Crashed" />
            </>
          )}
          <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px] ml-auto"
            style={{ color: "var(--brand-text)", opacity: 0.3 }}>
            Click model name to select row · click cell or column label to inspect
          </span>
        </div>
      </div>

      {/* ── detail panel ────────────────────────── */}
      {activeModelId && (
        <div className="mb-[40px]">
          <DetailPanel modelId={activeModelId} problemId={activeProblemId} mode={mode} />
        </div>
      )}
    </>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-[6px]">
      <div style={{ width: 10, height: 10, backgroundColor: color, flexShrink: 0 }} />
      <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px]"
        style={{ color: "var(--brand-text)", opacity: 0.45 }}>{label}</span>
    </div>
  );
}